// المستشار الذكي — مسار مقترح صارم: يقرأ التخصص والجدارات والتعليم والشهادات الحالية،
// ثم يعرض فقط ما يسد فجوة فعلية، مع سبب كل خطوة، وما استُبعد ولماذا.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePathMotion, useReveal } from "@/v2/lib/motion";
import { RefreshCw, GraduationCap, Plus, ChevronDown, ArrowLeft } from "lucide-react";

const LEVELS = ["ثانوي", "دبلوم", "بكالوريوس", "ماجستير", "دكتوراه"];

/** The authority's eight-point star: two squares, one turned 45°. */
export const Star = ({ filled = false }) => (
  <svg viewBox="0 0 28 28" aria-hidden="true">
    <g transform="translate(14 14)" fill={filled ? "currentColor" : "rgb(var(--surface))"} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
      <rect x="-8.5" y="-8.5" width="17" height="17" />
      <rect x="-8.5" y="-8.5" width="17" height="17" transform="rotate(45)" />
    </g>
    {!filled && <circle cx="14" cy="14" r="2.4" fill="currentColor" />}
  </svg>
);

const useAdvisor = () => useQuery({ queryKey: ["masar-advisor"], queryFn: async () => (await api.get("/masar/advisor")).data, staleTime: 60_000 });

function Background({ data, onAddCert }) {
  const qc = useQueryClient();
  const known = data.education?.level && data.education.level !== "غير محدد";
  const [open, setOpen] = useState(!known);
  const [level, setLevel] = useState(known ? data.education.level : "");
  const [field, setField] = useState(data.education?.field !== "غير محدد" ? data.education?.field || "" : "");
  const save = useMutation({
    mutationFn: async () => (await api.put("/masar/advisor/background", { education_level: level, education_field: field })).data,
    onSuccess: (fresh) => { qc.setQueryData(["masar-advisor"], fresh); setOpen(false); toast.success("حُدّث مسارك بناءً على مؤهلك"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "تعذر الحفظ، حاول مرة أخرى"),
  });
  return (
    <div className="bg-surface-2 rounded-xl px-4 py-3.5 mt-6" data-reveal>
      <div className="flex items-center gap-3 flex-wrap">
        <GraduationCap size={18} className="text-beige-ink shrink-0" />
        <div className="flex-1 min-w-[200px] text-sm">
          {known ? <>مؤهلك: <b>{data.education.level}</b>{data.education.field !== "غير محدد" && <> في {data.education.field}</>}</>
            : <>أضف مؤهلك التعليمي ليستبعد المستشار ما لا تحتاجه.</>}
          <span className="text-ink-3"> · {data.held_count ? `${data.held_count} شهادة مرسلة أو معتمدة` : "تُحتسب شهاداتك بعد إرسالها للمراجعة"}</span>
        </div>
        <button className="v2-btn-ghost v2-btn-sm" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="advisor-edit-education">{known ? "تعديل المؤهل" : "إضافة المؤهل"}</button>
        <button className="v2-btn-ghost v2-btn-sm" onClick={onAddCert} data-testid="advisor-add-cert"><Plus size={15} /> شهادة أملكها</button>
      </div>
      {open && (
        <form className="grid sm:grid-cols-[180px_1fr_auto] gap-3 mt-4 items-end" onSubmit={(e) => { e.preventDefault(); save.mutate(); }} data-testid="advisor-education-form">
          <label><span className="v2-label">المستوى التعليمي</span>
            <select className="v2-input" value={level} onChange={(e) => setLevel(e.target.value)} required>
              <option value="" disabled>اختر</option>{LEVELS.map((l) => <option key={l}>{l}</option>)}
            </select>
          </label>
          <label><span className="v2-label">التخصص</span>
            <input className="v2-input" value={field} maxLength={80} onChange={(e) => setField(e.target.value)} placeholder="مثل: إدارة الموارد البشرية" />
          </label>
          <button className="v2-btn" disabled={save.isPending || !level}>{save.isPending ? "جارٍ التحديث…" : "حفظ وتحديث المسار"}</button>
        </form>
      )}
    </div>
  );
}

export default function AdvisorPanel({ onOpenGuide, onAddCert }) {
  const qc = useQueryClient();
  const { data, isLoading } = useAdvisor();
  const recs = data?.recommendations || [];
  const rail = usePathMotion([recs.length]);
  const scope = useReveal([data?.generated_at]);
  const [showSkipped, setShowSkipped] = useState(false);
  const refresh = useMutation({
    mutationFn: async () => (await api.post("/masar/advisor/refresh")).data,
    onSuccess: (fresh) => qc.setQueryData(["masar-advisor"], fresh),
    onError: () => toast.error("تعذر تحديث الاقتراحات الآن"),
  });

  if (isLoading) return <div className="v2-glass p-6"><div className="v2-skel h-6 w-48 mb-5" /><div className="v2-skel h-20 mb-3" /><div className="v2-skel h-20" /></div>;
  if (!data?.available) return null;
  const skipped = data.skipped || [];
  const ai = data.mode === "ai";

  return (
    <section ref={scope} className="v2-glass p-5 sm:p-7" data-testid="advisor-panel" aria-labelledby="advisor-title">
      <div className="flex items-start gap-4 flex-wrap" data-reveal>
        <div className="flex-1 min-w-[240px]">
          <h2 id="advisor-title" className="m-0">مسارك المقترح</h2>
          <p className="text-ink-2 text-[0.95rem] mt-1 mb-0">
            {data.summary || `مبني على تخصص «${data.job?.specialty}» ومرتبتك وما تملكه من شهادات.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="state-chip held" title={ai ? "اختار المستشار الذكي من القائمة المعتمدة لوظيفتك" : "طُبقت قواعد تخصصك ومرتبتك"}><i />{ai ? "المستشار الذكي" : "قواعد التخصص"}</span>
          <button className="v2-btn-ghost v2-btn-sm" onClick={() => refresh.mutate()} disabled={refresh.isPending} aria-label="تحديث الاقتراحات" data-testid="advisor-refresh">
            <RefreshCw size={15} className={refresh.isPending ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {recs.length === 0 ? (
        <div className="mt-6 v2-inset p-5" data-reveal>
          <div className="font-semibold">مسارك مكتمل حاليًا</div>
          <p className="text-sm text-ink-2 m-0 mt-1">كل ما يحتاجه تخصصك تملكه أو تملك ما يعادله. ستظهر هنا أي متطلبات جديدة.</p>
        </div>
      ) : (
        <ol ref={rail} className="path-rail list-none p-0 mt-6 mb-0 space-y-3" data-testid="advisor-path">
          <span className="path-rail-line" aria-hidden="true" />
          {recs.map((r, i) => (
            <li key={r.id} className="path-step relative" data-state={i === 0 ? "next" : "pending"} data-reveal>
              <span className="path-star"><Star filled={i === 0} /></span>
              <div className={`rounded-xl border p-4 sm:p-5 transition-colors ${i === 0 ? "border-beige/60 bg-beige-soft/60" : "border-line bg-surface hover:border-beige/50"}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="text-sm text-ink-3">{r.track}</div>
                    <h3 className="m-0 mt-0.5 text-[1.25rem]">{r.title}</h3>
                    <div className="text-sm text-ink-2 mt-0.5">{r.kind === "academic" ? "مؤهل تعليمي" : "شهادة احترافية"}{r.issuer && !r.issuer.includes("غير محددة") && <> · {r.issuer}</>}</div>
                  </div>
                  {i === 0 && <span className="state-chip next"><i />خطوتك التالية</span>}
                </div>
                <p className="text-[0.95rem] mt-3 mb-0">{r.reason}</p>
                {r.equivalents?.length > 0 && <p className="text-sm text-ink-2 mt-2 mb-0">أو ما يعادلها، يكفي أحدها: {r.equivalents.join("، ")}</p>}
                <div className="mt-4">
                  <button className={i === 0 ? "v2-btn v2-btn-sm" : "v2-btn-ghost v2-btn-sm"} onClick={() => onOpenGuide?.(r)} data-testid={`advisor-open-${i}`}>
                    تفاصيل التسجيل <ArrowLeft size={15} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {skipped.length > 0 && (
        <div className="mt-5" data-reveal>
          <button className="text-sm text-ink-2 hover:text-ink inline-flex items-center gap-1.5" onClick={() => setShowSkipped((v) => !v)} aria-expanded={showSkipped} data-testid="advisor-skipped-toggle">
            <ChevronDown size={16} className={`transition-transform ${showSkipped ? "rotate-180" : ""}`} />
            استبعد المستشار {skipped.length} {skipped.length === 1 ? "خطوة" : "خطوات"} لأنك لا تحتاجها
          </button>
          {showSkipped && (
            <ul className="list-none p-0 mt-3 space-y-2">
              {skipped.map((s) => (
                <li key={s.id} className="v2-inset px-4 py-3 flex items-start gap-3">
                  <span className="state-chip done shrink-0"><i />مستوفى</span>
                  <div className="text-sm"><b>{s.title}</b><div className="text-ink-2">{s.reason}</div></div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Background data={data} onAddCert={onAddCert} />
    </section>
  );
}
